import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  Edit2,
  Loader2,
  Package,
  Plus,
  Save,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDate, formatMoney, formatPercent, formatPeriod } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/customers/${id}`);
      setData(r.data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Kunde …
      </div>
    );
  if (error || !data) return <div className="p-8 text-rose-600">{error || "Kunde nicht gefunden."}</div>;

  const { customer, products, monthly_summary, commission_rates, lifetime_days, total_commission } = data;

  return (
    <div data-testid="customer-detail-page">
      <PageHeader
        title={customer.name}
        subtitle={customer.auto_created ? "Automatisch erstellt" : "Manuell angelegt"}
      >
        <Link
          to="/customers"
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm"
        >
          <ArrowLeft size={14} /> Zurück zur Kundenliste
        </Link>
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Stat label="Status"><StatusBadge status={customer.status} /></Stat>
          <Stat label="Lifetime" mono>
            {lifetime_days != null ? `${lifetime_days} Tage` : "—"}
          </Stat>
          <Stat label="Provision total" mono>{formatMoney(total_commission)}</Stat>
        </div>

        <StartDatesSection customer={customer} onChange={reload} />

        <CustomerLifecycleSection customer={customer} onChange={reload} />

        <ProductsSection
          customerId={customer.id}
          products={products}
          onChange={reload}
        />

        <CommissionRatesSection rates={commission_rates} onChange={reload} />

        <MonthlySummarySection monthly={monthly_summary} />
      </div>
    </div>
  );
}

function Stat({ label, children, mono }) {
  return (
    <div className="kpi-card p-4 rounded-sm">
      <div className="kbd-label mb-1.5">{label}</div>
      <div className={mono ? "font-mono font-semibold text-lg" : "text-lg"}>{children}</div>
    </div>
  );
}

function StartDatesSection({ customer, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    expected_first_payment_provision_date: customer.expected_first_payment_provision_date || "",
    expected_first_license_provision_date: customer.expected_first_license_provision_date || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        expected_first_payment_provision_date: form.expected_first_payment_provision_date || null,
        expected_first_license_provision_date: form.expected_first_license_provision_date || null,
      };
      await api.patch(`/customers/${customer.id}`, payload);
      toast.success("Erwartete Daten gespeichert");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const paymentMismatch =
    customer.expected_first_payment_provision_date &&
    customer.first_payment_provision_date &&
    customer.expected_first_payment_provision_date !== customer.first_payment_provision_date;
  const licenseMismatch =
    customer.expected_first_license_provision_date &&
    customer.first_license_provision_date &&
    customer.expected_first_license_provision_date !== customer.first_license_provision_date;

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="start-dates-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-slate-500" />
          <h3 className="font-display text-lg font-bold">Start-Datum (Ist vs. Soll)</h3>
        </div>
        {!editing ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="rounded-sm"
            data-testid="dates-edit-btn"
          >
            <Edit2 size={14} className="mr-1.5" /> Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="dates-save-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        )}
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        <DateRow
          label="Payment-Provision"
          actual={customer.first_payment_provision_date}
          expected={customer.expected_first_payment_provision_date}
          mismatch={paymentMismatch}
          editing={editing}
          value={form.expected_first_payment_provision_date}
          onChange={(v) =>
            setForm({ ...form, expected_first_payment_provision_date: v })
          }
          testid="payment-date"
        />
        <DateRow
          label="Lizenz-Provision"
          actual={customer.first_license_provision_date}
          expected={customer.expected_first_license_provision_date}
          mismatch={licenseMismatch}
          editing={editing}
          value={form.expected_first_license_provision_date}
          onChange={(v) =>
            setForm({ ...form, expected_first_license_provision_date: v })
          }
          testid="license-date"
        />
      </div>
    </div>
  );
}

function DateRow({ label, actual, expected, mismatch, editing, value, onChange, testid }) {
  return (
    <div className="space-y-2" data-testid={`date-row-${testid}`}>
      <div className="kbd-label">{label}</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Ist (aus Reports)</div>
          <div className="font-mono text-sm font-semibold">{formatDate(actual)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Erwartet</div>
          {editing ? (
            <Input
              type="date"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 text-sm"
              data-testid={`date-input-${testid}`}
            />
          ) : (
            <div className="font-mono text-sm">{expected ? formatDate(expected) : "—"}</div>
          )}
        </div>
      </div>
      {mismatch && !editing && (
        <div className="flex items-center gap-1.5 text-xs text-rose-700 font-medium mt-1">
          <span>⚠</span> Soll-Datum weicht ab
        </div>
      )}
    </div>
  );
}

function CustomerLifecycleSection({ customer, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    end_date: customer.end_date || "",
    expected_end_date: customer.expected_end_date || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, {
        end_date: form.end_date || null,
        expected_end_date: form.expected_end_date || null,
      });
      toast.success("Kunden-Ende gespeichert");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const churned = !!customer.end_date;
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="lifecycle-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-slate-500" />
          <h3 className="font-display text-lg font-bold">Kunden-Ende</h3>
          {churned && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-semibold uppercase tracking-wider border rounded-sm bg-rose-50 text-rose-700 border-rose-200">
              Abgewandert
            </span>
          )}
        </div>
        {!editing ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="rounded-sm"
            data-testid="lifecycle-edit-btn"
          >
            <Edit2 size={14} className="mr-1.5" /> Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="lifecycle-save-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        )}
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-1.5" data-testid="end-date-actual">
          <div className="kbd-label">Tatsächliches Ende</div>
          {editing ? (
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="h-9 text-sm"
              data-testid="end-date-input"
            />
          ) : (
            <div className="font-mono text-sm font-semibold">
              {customer.end_date ? formatDate(customer.end_date) : "—"}
            </div>
          )}
          <div className="text-xs text-slate-500">
            Solange leer wird der Kunde in fehlende-Kunden-Reports geprüft.
          </div>
        </div>
        <div className="space-y-1.5" data-testid="end-date-expected">
          <div className="kbd-label">Geplantes/erwartetes Ende</div>
          {editing ? (
            <Input
              type="date"
              value={form.expected_end_date}
              onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })}
              className="h-9 text-sm"
              data-testid="expected-end-date-input"
            />
          ) : (
            <div className="font-mono text-sm">
              {customer.expected_end_date ? formatDate(customer.expected_end_date) : "—"}
            </div>
          )}
          <div className="text-xs text-slate-500">
            z. B. wenn Kündigung bekannt aber noch nicht aktiv.
          </div>
        </div>
      </div>
    </div>
  );
}


function ProductsSection({ customerId, products, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    base_price: "",
    expected_price: "",
    expected_rate: "30",
    end_date: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      base_price: "",
      expected_price: "",
      expected_rate: "30",
      end_date: "",
      notes: "",
    });
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      base_price: String(p.base_price ?? ""),
      expected_price: String(p.expected_price ?? ""),
      expected_rate: String(p.expected_rate ?? "30"),
      end_date: p.end_date || "",
      notes: p.notes ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        base_price: parseFloat(form.base_price || "0") || 0,
        expected_price: parseFloat(form.expected_price || "0") || 0,
        expected_rate: parseFloat(form.expected_rate || "0") || 0,
        end_date: form.end_date || null,
        notes: form.notes,
      };
      if (editing) {
        await api.patch(`/customers/${customerId}/products/${editing.id}`, body);
      } else {
        await api.post(`/customers/${customerId}/products`, body);
      }
      toast.success(editing ? "Produkt aktualisiert" : "Produkt angelegt");
      setOpen(false);
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Produkt „${p.name}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/customers/${customerId}/products/${p.id}`);
      toast.success("Produkt gelöscht");
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="products-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-slate-500" />
          <h3 className="font-display text-lg font-bold">Produkte &amp; Soll-Preise</h3>
        </div>
        <Button
          size="sm"
          onClick={startCreate}
          className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          data-testid="product-add-btn"
        >
          <Plus size={14} className="mr-1" /> Produkt anlegen
        </Button>
      </div>
      {products.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center">
          Noch keine Produkte erfasst. Über „Produkt anlegen“ kannst Du Sollpreise und Provisionssätze hinterlegen.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Name</th>
                <th className="text-right px-3 py-2 font-semibold">Basispreis</th>
                <th className="text-right px-3 py-2 font-semibold">Soll-Preis</th>
                <th className="text-right px-3 py-2 font-semibold">Soll-Satz</th>
                <th className="text-left px-3 py-2 font-semibold">Aktiv von</th>
                <th className="text-left px-3 py-2 font-semibold">Zuletzt</th>
                <th className="text-left px-3 py-2 font-semibold">End-Datum</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {[...products].sort((a, b) => (a.product_number || 9999) - (b.product_number || 9999)).map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-slate-100 ${p.end_date ? "opacity-60" : ""}`}
                  data-testid={`product-row-${p.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">
                    {p.product_number || "—"}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {p.name}
                    {p.auto_created && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-600 font-semibold">
                        auto
                      </span>
                    )}
                    {p.notes && (
                      <div className="text-xs text-slate-500 mt-0.5">{p.notes}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {p.base_price ? formatMoney(p.base_price) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {p.expected_price ? formatMoney(p.expected_price) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{formatPercent(p.expected_rate)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.first_seen_period || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.last_seen_period || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {p.end_date ? formatDate(p.end_date) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => startEdit(p)}
                      className="text-slate-400 hover:text-slate-900"
                      data-testid={`product-edit-${p.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => del(p)}
                      className="text-slate-400 hover:text-rose-600"
                      data-testid={`product-delete-${p.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Produkt bearbeiten" : "Produkt anlegen"}</DialogTitle>
            <DialogDescription>
              Sollpreis und Soll-Provisionssatz helfen, in Reports Abweichungen automatisch zu erkennen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="pname">Produktname</Label>
              <Input
                id="pname"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z. B. Lightspeed Restaurant K-Series"
                data-testid="product-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="pbase">Basispreis (EUR)</Label>
                <Input
                  id="pbase"
                  type="number"
                  step="0.01"
                  value={form.base_price}
                  onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                  data-testid="product-base-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pprice">Soll-Preis (EUR)</Label>
                <Input
                  id="pprice"
                  type="number"
                  step="0.01"
                  value={form.expected_price}
                  onChange={(e) => setForm({ ...form, expected_price: e.target.value })}
                  data-testid="product-price-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="prate">Soll-Provisionssatz (%)</Label>
                <Input
                  id="prate"
                  type="number"
                  step="0.1"
                  value={form.expected_rate}
                  onChange={(e) => setForm({ ...form, expected_rate: e.target.value })}
                  data-testid="product-rate-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pend">End-Datum</Label>
                <Input
                  id="pend"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="product-end-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pnotes">Notizen</Label>
              <Textarea
                id="pnotes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="product-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={save}
              disabled={busy || !form.name.trim()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="product-save-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommissionRatesSection({ rates, onChange }) {
  const [edits, setEdits] = useState({});

  const saveRow = async (rateId) => {
    const e = edits[rateId];
    if (!e) return;
    try {
      const body = {};
      if (e.expected_rate !== undefined) body.expected_rate = parseFloat(e.expected_rate || "0");
      if (e.expected_amount !== undefined) body.expected_amount = parseFloat(e.expected_amount || "0");
      await api.patch(`/reports/commission-rates/${rateId}`, body);
      toast.success("Soll-Werte aktualisiert");
      setEdits((prev) => {
        const { [rateId]: _, ...rest } = prev;
        return rest;
      });
      onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (!rates?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="commission-rates-section">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
        <CalendarClock size={16} className="text-slate-500" />
        <h3 className="font-display text-lg font-bold">Monatliche Provisionssätze</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2 font-semibold">Periode</th>
              <th className="text-left px-4 py-2 font-semibold">Typ</th>
              <th className="text-right px-4 py-2 font-semibold">Basis</th>
              <th className="text-right px-4 py-2 font-semibold">Ist-Satz</th>
              <th className="text-right px-4 py-2 font-semibold">Soll-Satz</th>
              <th className="text-right px-4 py-2 font-semibold">Ist-Provision</th>
              <th className="text-right px-4 py-2 font-semibold">Soll-Provision</th>
              <th className="text-right px-4 py-2 font-semibold">Diff.</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rates.sort((a, b) => b.period_month.localeCompare(a.period_month)).map((r) => {
              const draft = edits[r.id] || {};
              const editing = draft.editing;
              return (
                <tr
                  key={r.id}
                  className={`border-b border-slate-100 ${r.has_discrepancy ? "discrepancy-row" : ""}`}
                  data-testid={`rate-row-${r.id}`}
                >
                  <td className="px-4 py-2 font-mono">{r.period_month}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.type} /></td>
                  <td className="px-4 py-2 text-right font-mono text-slate-500">
                    {/* commissionable not stored at rate level - leave dash */}—
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{formatPercent(r.actual_rate)}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {editing ? (
                      <Input
                        type="number"
                        step="0.1"
                        defaultValue={r.expected_rate}
                        onChange={(e) =>
                          setEdits((p) => ({
                            ...p,
                            [r.id]: { ...p[r.id], expected_rate: e.target.value },
                          }))
                        }
                        className="h-7 text-right w-20 inline-block"
                      />
                    ) : (
                      formatPercent(r.expected_rate)
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">
                    {formatMoney(r.actual_amount)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {editing ? (
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={r.expected_amount}
                        onChange={(e) =>
                          setEdits((p) => ({
                            ...p,
                            [r.id]: { ...p[r.id], expected_amount: e.target.value },
                          }))
                        }
                        className="h-7 text-right w-24 inline-block"
                      />
                    ) : (
                      formatMoney(r.expected_amount || 0)
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {r.has_discrepancy ? (
                      <span className="text-rose-700 font-semibold">
                        {formatMoney(r.discrepancy_amount, true)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {editing ? (
                      <button
                        onClick={() => saveRow(r.id)}
                        className="text-emerald-600 hover:text-emerald-800"
                        data-testid={`rate-save-${r.id}`}
                      >
                        <Save size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          setEdits((p) => ({ ...p, [r.id]: { ...(p[r.id] || {}), editing: true } }))
                        }
                        className="text-slate-400 hover:text-slate-900"
                        data-testid={`rate-edit-${r.id}`}
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlySummarySection({ monthly }) {
  if (!monthly?.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6 text-center text-sm text-slate-500">
        Noch keine Provisionseinträge.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="monthly-section">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
        <TrendingUp size={16} className="text-slate-500" />
        <h3 className="font-display text-lg font-bold">Provisions-Timeline</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <th className="text-left px-4 py-2 font-semibold">Periode</th>
            <th className="text-left px-4 py-2 font-semibold">Typ</th>
            <th className="text-right px-4 py-2 font-semibold">Provisions-Basis</th>
            <th className="text-right px-4 py-2 font-semibold">Provision</th>
            <th className="text-right px-4 py-2 font-semibold">Einträge</th>
          </tr>
        </thead>
        <tbody>
          {monthly.map((m, idx) => (
            <tr key={idx} className="border-b border-slate-100">
              <td className="px-4 py-2 font-mono">{formatPeriod(m.period_month, m.period_label)}</td>
              <td className="px-4 py-2"><StatusBadge status={m.type} /></td>
              <td className="px-4 py-2 text-right font-mono">{formatMoney(m.commissionable_amount)}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{formatMoney(m.commission_amount)}</td>
              <td className="px-4 py-2 text-right font-mono">{m.entry_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

